import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { CatalogAPI } from '../lib/api';
import { useSiteConfig } from '../context/SiteConfigContext';

const Footer = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const { config } = useSiteConfig();
  useEffect(() => { CatalogAPI.categories().then(setCategories).catch(() => {}); }, []);

  const siteTitle = config['site.title'];
  const tagline = config['site.tagline'];
  const phone = config['site.contact.phone'];
  const phone2 = config['site.contact.phone_2'];
  const email = config['site.contact.email'];
  const email2 = config['site.contact.email_2'];
  const address = config['site.contact.address'];
  const fb = config['site.social.facebook'];
  const ig = config['site.social.instagram'];
  const li = config['site.social.linkedin'];

  return (
    <footer className="bg-[#1F4D3F] text-[#E8E0D0]">
      {/* Contact band */}
      <div className="border-b border-[#2D6452]">
        <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <h3 className="font-serif text-3xl text-white font-semibold">{t('footer.questions_title')}</h3>
            <p className="text-[#C9BFAE] mt-2">{t('footer.questions_desc')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-[#173B30] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[#C9BFAE]">{t('footer.advice')}</div>
              <a href={`tel:${phone}`} className="flex items-center gap-2 mt-2 text-white hover:underline">
                <Phone className="w-4 h-4" /> {phone}
              </a>
              <a href={`mailto:${email}`} className="flex items-center gap-2 mt-1 text-white text-sm hover:underline">
                <Mail className="w-4 h-4" /> {email}
              </a>
            </div>
            <div className="bg-[#173B30] rounded-xl p-4">
              <div className="text-xs uppercase tracking-wider text-[#C9BFAE]">{t('footer.products_suppliers')}</div>
              <a href={`tel:${phone2 || phone}`} className="flex items-center gap-2 mt-2 text-white hover:underline">
                <Phone className="w-4 h-4" /> {phone2 || phone}
              </a>
              <a href={`mailto:${email2 || email}`} className="flex items-center gap-2 mt-1 text-white text-sm hover:underline">
                <Mail className="w-4 h-4" /> {email2 || email}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-12 grid grid-cols-2 md:grid-cols-5 gap-8">
        <div className="col-span-2">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center">
              <span className="font-serif text-[#1F4D3F] text-xl font-semibold">{(siteTitle || 'C')[0]}</span>
            </div>
            <div>
              <div className="font-serif text-2xl font-semibold text-white">{siteTitle}</div>
              <div className="text-[10px] text-[#C9BFAE] tracking-wider uppercase">{tagline}</div>
            </div>
          </Link>
          <p className="mt-4 text-sm text-[#C9BFAE] max-w-md">
            {t('footer.description')}
          </p>
          <div className="mt-6 flex items-center gap-2 text-sm text-[#C9BFAE]">
            <MapPin className="w-4 h-4" /> {address}
          </div>
          <div className="mt-4 flex items-center gap-2">
            {fb && <a href={fb} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[#173B30] flex items-center justify-center hover:bg-[#2D6452]" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>}
            {ig && <a href={ig} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[#173B30] flex items-center justify-center hover:bg-[#2D6452]" aria-label="Instagram"><Instagram className="w-4 h-4" /></a>}
            {li && <a href={li} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-[#173B30] flex items-center justify-center hover:bg-[#2D6452]" aria-label="LinkedIn"><Linkedin className="w-4 h-4" /></a>}
            {!fb && !ig && !li && (
              <span className="text-xs text-[#C9BFAE]/60">{t('footer.no_social')}</span>
            )}
          </div>
        </div>

        <div>
          <h4 className="text-white font-medium mb-4">{t('footer.categories')}</h4>
          <ul className="space-y-2 text-sm">
            {categories.slice(0, 6).map((c) => (
              <li key={c.id}><Link to={`/categorie/${c.id}`} className="text-[#C9BFAE] hover:text-white">{c.name}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white font-medium mb-4">{t('footer.platform')}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/inscription?role=supplier" className="text-[#C9BFAE] hover:text-white">{t('common.become_supplier')}</Link></li>
            <li><Link to="/inscription?role=reparateur" className="text-[#C9BFAE] hover:text-white">Devenir Technicien</Link></li>
            <li><Link to="/occasion" className="text-[#C9BFAE] hover:text-white">Matériel d'occasion</Link></li>
            <li><Link to="/reparateurs" className="text-[#C9BFAE] hover:text-white">Réparateurs agréés</Link></li>
            <li><Link to="/comment-ca-marche" className="text-[#C9BFAE] hover:text-white">{t('footer.how_it_works')}</Link></li>
            <li><Link to="/tarifs" className="text-[#C9BFAE] hover:text-white">{t('footer.pricing')}</Link></li>
            <li><Link to="/blog" className="text-[#C9BFAE] hover:text-white">{t('footer.blog')}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-medium mb-4">{t('footer.help')}</h4>
          <ul className="space-y-2 text-sm">
            <li><Link to="/contact" className="text-[#C9BFAE] hover:text-white">{t('footer.contact')}</Link></li>
            <li><Link to="/faq" className="text-[#C9BFAE] hover:text-white">{t('footer.faq')}</Link></li>
            <li><Link to="/livraison" className="text-[#C9BFAE] hover:text-white">{t('footer.shipping')}</Link></li>
            <li><Link to="/cgv" className="text-[#C9BFAE] hover:text-white">{t('footer.terms')}</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-[#2D6452]">
        <div className="max-w-7xl mx-auto px-4 py-5 flex flex-col md:flex-row justify-between gap-2 text-xs text-[#C9BFAE]">
          <div>© {new Date().getFullYear()} {siteTitle} — {t('footer.rights')}</div>
          <div className="flex items-center gap-4">
            <Link to="/mentions-legales" className="hover:text-white">{t('footer.legal')}</Link>
            <Link to="/confidentialite" className="hover:text-white">{t('footer.privacy')}</Link>
            <Link to="/cookies" className="hover:text-white">{t('footer.cookies')}</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
